import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { BaseCRUDManager } from '../models/base/BaseCRUDManager';
import { sendEmail } from './emailService';
import { verifyEmailAsync } from '../utilities/emailEncryption';
import { isValidEmail } from '../utilities/formValidation';
import type { ITeamUpdateGroup } from '../types/TeamUpdate';
import { OVERHEAR_ORGANISATION_ID } from '../constants/organisations';

const ACTIVITY = 'teamUpdateMemberActivity';
const USERS = 'Users';

const ADMIN_BASE_URL =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://admin.theoverhear.app';

export type TeamUpdateMemberActivity = {
  id: string;
  groupId: string;
  userId: string;
  /** Chat opened / messages marked seen (read receipts). */
  lastViewedAt?: Timestamp;
  /** Client has received messages through this timestamp (delivery receipts). */
  lastDeliveredAt?: Timestamp;
  lastWeeklyEmailAt?: Timestamp;
};

function timestampMillis(value: Timestamp | undefined): number {
  if (!value) return 0;
  try {
    return value.toMillis();
  } catch {
    return 0;
  }
}

/** Client-side watermark to avoid repeated Firestore reads on live snapshots. */
const deliveryWatermarkMs = new Map<string, number>();
const viewWatermarkMs = new Map<string, number>();

export function teamUpdateActivityId(groupId: string, userId: string): string {
  return `${groupId}_${userId}`;
}

function chatUrl(groupId: string): string {
  return `${ADMIN_BASE_URL}/team-updates/${groupId}`;
}

async function resolveUserEmail(
  userId: string,
  organisationId: string = OVERHEAR_ORGANISATION_ID
): Promise<{ email: string; name: string } | null> {
  const snap = await getDoc(doc(db, USERS, userId));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  const profile = (data.userProfile || {}) as { userName?: string };
  const name =
    (typeof profile.userName === 'string' && profile.userName) ||
    (typeof data.email === 'string' && data.email.includes('@') ? data.email : '') ||
    'there';

  const candidates: string[] = [];
  if (typeof data.email === 'string' && data.email.trim()) {
    candidates.push(data.email.trim());
  }
  const orgs = (data.Organisations || {}) as Record<string, { email?: string; name?: string }>;
  const orgEmail = orgs[organisationId]?.email;
  if (typeof orgEmail === 'string' && orgEmail.trim()) {
    candidates.push(orgEmail.trim());
  }
  if (typeof orgs[organisationId]?.name === 'string' && orgs[organisationId].name) {
    // prefer org display name when present
  }

  for (const candidate of candidates) {
    try {
      if (isValidEmail(candidate)) {
        return { email: candidate.trim().toLowerCase(), name: orgs[organisationId]?.name || name };
      }
      const decrypted = await verifyEmailAsync(candidate);
      if (decrypted && isValidEmail(decrypted)) {
        return {
          email: decrypted.trim().toLowerCase(),
          name: orgs[organisationId]?.name || name,
        };
      }
    } catch {
      // try next candidate
    }
  }
  return null;
}

function addedEmailBody(params: {
  memberName: string;
  groupName: string;
  description?: string;
  atAGlance?: string;
  groupId: string;
}): { subject: string; text: string; html: string } {
  const link = chatUrl(params.groupId);
  const subject = `You've been added to ${params.groupName} on Team updates`;
  const text = [
    `Hi ${params.memberName},`,
    '',
    `You've been added to the Team updates chat “${params.groupName}”.`,
    params.description ? `\n${params.description}\n` : '',
    params.atAGlance ? `At a glance: ${params.atAGlance}\n` : '',
    `Open the chat: ${link}`,
    '',
    '— OVERHEAR',
  ]
    .filter(Boolean)
    .join('\n');

  const html = `
    <p>Hi ${escapeHtml(params.memberName)},</p>
    <p>You've been added to the Team updates chat <strong>${escapeHtml(params.groupName)}</strong>.</p>
    ${params.description ? `<p>${escapeHtml(params.description)}</p>` : ''}
    ${
      params.atAGlance
        ? `<p><strong>At a glance:</strong> ${escapeHtml(params.atAGlance)}</p>`
        : ''
    }
    <p><a href="${link}">Open the chat</a></p>
    <p>— OVERHEAR</p>
  `.trim();

  return { subject, text, html };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Email newly added members that they have been added to a Team updates chat.
 */
export async function notifyTeamUpdateMembersAdded(params: {
  group: Pick<ITeamUpdateGroup, 'id' | 'name' | 'description' | 'atAGlance' | 'organisationId'>;
  memberIds: string[];
  excludeUserId?: string;
}): Promise<{ sent: number; failed: number }> {
  const targets = Array.from(new Set(params.memberIds.filter(Boolean))).filter(
    (id) => id !== params.excludeUserId
  );
  let sent = 0;
  let failed = 0;

  for (const userId of targets) {
    try {
      const resolved = await resolveUserEmail(userId, params.group.organisationId);
      if (!resolved) {
        failed += 1;
        continue;
      }
      const body = addedEmailBody({
        memberName: resolved.name.split(' ')[0] || resolved.name,
        groupName: params.group.name,
        description: params.group.description,
        atAGlance: params.group.atAGlance,
        groupId: params.group.id,
      });
      await sendEmail({
        to: resolved.email,
        subject: body.subject,
        text: body.text,
        html: body.html,
      });
      sent += 1;
    } catch (error) {
      console.error('Failed to send team-update added email', { userId, error });
      failed += 1;
    }
  }

  return { sent, failed };
}

/** Record that the user opened a chat (used for weekly inactivity emails + seen ticks). */
export async function recordTeamUpdateGroupView(
  groupId: string,
  userId: string
): Promise<void> {
  if (!groupId || !userId) return;
  const id = teamUpdateActivityId(groupId, userId);
  const now = Timestamp.now();
  const nowMs = timestampMillis(now);
  const cachedView = viewWatermarkMs.get(id) || 0;
  const cachedDelivery = deliveryWatermarkMs.get(id) || 0;
  // Avoid hammering Firestore while the feed stays open / remounts.
  if (cachedView > 0 && nowMs - cachedView < 15_000 && cachedDelivery >= nowMs) {
    return;
  }

  const ref = doc(db, ACTIVITY, id);
  const existing = await getDoc(ref);
  const existingData = existing.exists()
    ? (existing.data() as Partial<TeamUpdateMemberActivity>)
    : null;
  const existingDeliveryMs = Math.max(
    timestampMillis(existingData?.lastDeliveredAt),
    timestampMillis(existingData?.lastViewedAt),
    cachedDelivery
  );
  const deliveredAt =
    existingDeliveryMs > nowMs
      ? existingData?.lastDeliveredAt || Timestamp.fromMillis(existingDeliveryMs)
      : now;

  await setDoc(
    ref,
    {
      groupId,
      userId,
      lastViewedAt: now,
      lastDeliveredAt: deliveredAt,
    },
    { merge: true }
  );
  viewWatermarkMs.set(id, nowMs);
  deliveryWatermarkMs.set(id, Math.max(existingDeliveryMs, nowMs));
}

/**
 * Record that the user's client has received messages in a chat (double-tick delivery).
 * `deliveredThrough` should be the latest observed message `createdAt` (or now).
 * No-ops when the stored watermark is already at least as recent.
 */
export async function recordTeamUpdateGroupDelivery(
  groupId: string,
  userId: string,
  deliveredThrough: Timestamp = Timestamp.now()
): Promise<void> {
  if (!groupId || !userId) return;
  const throughMs = timestampMillis(deliveredThrough);
  if (!throughMs) return;

  const id = teamUpdateActivityId(groupId, userId);
  const cached = deliveryWatermarkMs.get(id) || 0;
  if (cached >= throughMs) return;

  const ref = doc(db, ACTIVITY, id);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const data = existing.data() as Partial<TeamUpdateMemberActivity>;
    const prevMs = Math.max(
      timestampMillis(data.lastDeliveredAt),
      timestampMillis(data.lastViewedAt)
    );
    deliveryWatermarkMs.set(id, Math.max(prevMs, cached));
    if (prevMs >= throughMs) return;
    await setDoc(
      ref,
      {
        groupId,
        userId,
        lastDeliveredAt: deliveredThrough,
      },
      { merge: true }
    );
    deliveryWatermarkMs.set(id, throughMs);
    return;
  }

  await setDoc(ref, {
    groupId,
    userId,
    lastDeliveredAt: deliveredThrough,
  });
  deliveryWatermarkMs.set(id, throughMs);
}

export async function getTeamUpdateMemberActivity(
  groupId: string,
  userId: string
): Promise<TeamUpdateMemberActivity | null> {
  return BaseCRUDManager.getById<TeamUpdateMemberActivity>(
    ACTIVITY,
    teamUpdateActivityId(groupId, userId)
  );
}
